import { useFeatureFlagContext } from '@fluentsms/feature-flags';
import resolveEnv from '@fluentsms/utils';
import React, { ReactElement, useEffect } from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { tokenUtility } from '../token';

/**
 * MaterialUI has to be imported due to a complicated circular dependency
 * between @fluentsms/editors, @fluentsms/graphql, @fluentsms/auth, and @fluentsms/components
 */
// eslint-disable-next-line no-restricted-imports
import { Backdrop, CircularProgress } from '@material-ui/core';
import IdlePrompter from '../IdlePrompter';

export type AuthenticatedRouteProps = RouteProps;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DD_RUM: any;
  }
}
const environment = resolveEnv();

const getDatadogParameters = () => {
  const decodedToken = tokenUtility.getDecodedDetailToken();
  const jwtPayload = tokenUtility.getCompleteDecodedToken();
  return {
    id: decodedToken.userId,
    name: decodedToken.displayName,
    email: jwtPayload.email,
    plan: 'TBD',
    environment,
  };
};

const isDatadogReady = (): boolean => {
  return window.DD_RUM && typeof window.DD_RUM.onReady === 'function';
};

const bootstrapDatadog = () => {
  if (isDatadogReady()) {
    const datadogParameters = getDatadogParameters();
    window.DD_RUM.onReady(() => {
      window.DD_RUM.setUser(datadogParameters);
    });
  }
};

export const AuthenticatedRoute = ({ ...props }: AuthenticatedRouteProps): ReactElement => {
  const { isAuthenticated: getIsAuthenticated, isAuthenticating: getIsAuthenticating } = useAuth();
  const { pathname } = useLocation();
  const hashName = `${props.location?.pathname}${props.location?.search}`;
  const {
    isContextSet: isFeatureFlagContextSet,
    setUserContext: setFeatureFlagContext,
    clearUserContext: clearFeatureFlagContext,
  } = useFeatureFlagContext();
  const isAuthenticated = getIsAuthenticated();
  const isAuthenticating = getIsAuthenticating();

  useEffect(() => {
    // eslint-disable-next-line require-await
    const bootstrapDatadogAsync = async () => {
      if (isAuthenticated) {
        bootstrapDatadog();
      }
    };
    bootstrapDatadogAsync();
  }, [isAuthenticated]);

  useEffect(() => {
    // eslint-disable-next-line require-await
    const bootstrapLaunchDarklyAsync = async () => {
      if (isAuthenticated) {
        !isFeatureFlagContextSet &&
          setFeatureFlagContext(tokenUtility.getDecodedDetailToken(), tokenUtility.getCompleteDecodedToken());
      } else {
        clearFeatureFlagContext();
      }
    };
    bootstrapLaunchDarklyAsync();
  }, [clearFeatureFlagContext, isAuthenticated, setFeatureFlagContext, isFeatureFlagContextSet]);

  if (isAuthenticating || (isAuthenticated && !isFeatureFlagContextSet)) {
    return (
      <Backdrop role="status" aria-label="page loading" id="page-loading" data-testid="page-loading" invisible open>
        <CircularProgress color="primary" />
      </Backdrop>
    );
  }

  if (!isAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: '/login',
          search: pathname !== '/' ? `?${new URLSearchParams({ from: `${hashName}` }).toString()}` : undefined,
        }}
      />
    );
  }

  // if we are authenticated then start the inactivity countdown.
  return (
    <>
      <IdlePrompter />
      <Route {...props} />
    </>
  );
};

export default AuthenticatedRoute;
